import { z } from "zod";
import { COUNTRIES, countryCodeSchema } from "../countries.js";
import { CURRENCIES } from "../status.js";

/** Strict calendar date in the wire format used by all date-only DTO fields. */
export const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must use YYYY-MM-DD")
  .refine((value) => {
    const [yearText, monthText, dayText] = value.split("-");
    if (!yearText || !monthText || !dayText) return false;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    );
  }, "Date is not valid");
export type DateOnly = z.infer<typeof dateOnlySchema>;

/** Trim text and represent a blank value as null for nullable API fields. */
export const nullableText = (maximum: number) =>
  z.preprocess((value) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }, z.string().max(maximum).nullable().optional());

export const nullableDate = dateOnlySchema.nullable().optional();
export const currencySchema = z.enum(CURRENCIES);
export const countrySchema = countryCodeSchema;
export const countryCodeListSchema = z.array(z.enum(COUNTRIES));
export const versionSchema = z.number().int().positive();

export const nonNegativeAmountSchema = z
  .union([z.string(), z.number()])
  .pipe(z.coerce.number().finite().min(0));

export const uuidListSchema = z
  .array(z.string().uuid())
  .transform((values) => [...new Set(values)]);

/** Convert an optional text field to null when callers submit an empty string. */
export function normalizeNullableText(
  value: string | null | undefined,
): string | null | undefined {
  if (value === undefined || value === null) return value;
  const normalized = value.trim();
  return normalized.length === 0 ? null : normalized;
}
